import React from 'react';
import { useTranslation } from 'react-i18next';

import DefaultSubscription from './components/DefaultSubscription';
import ExpiredSubscription from './components/ExpiredSubscription';
import ExpiresSubscription from './components/ExpiresSubscription';
import PausedSubscription from './components/PausedSubscription';
import SubscriptionRedemption from './components/SubscriptionRedemption';
import UpdatingSubscription from './components/UpdatingSubscription';
import { ISubscriptionCard } from './types';
import useSubscriptionCard from './useSubscriptionCard';

const SubscriptionCard = ({
    onCancelSubscription,
    onReturnOffer,
    onRestoreSubscription,
    onResumeSubscription,
    onPauseSubscription,
    subscription,
}: ISubscriptionCard) => {
    const { t } = useTranslation();
    const {
        feeData,
        externalId,
        isExpiredSubscription,
        isExpiresSubscription,
        isSubscriptionRedemption,
        isSubscriptionPaused,
        isSubscriptionUpdating,
        isDiscountedSubscription,
        isReminderSet,
        isTimePassedSincePaused,
        handleCancelSubscription,
        handleRestoreSubscription,
        handleResumeSubscription,
        state,
        upcomingProduct,
        tabIcon,
        heading,
        subscriptionPeriod,
        price,
        expirationDate,
        isTrial,
        isMainSubscription,
        productCode,
        subscriptionPausePeriod,
        onShowReturnOffer,
        isOfferAvailableForSubscription,
        isReturnOfferApplied,
        pausedTo,
        isUpgrade,
        isNextChargeAvailable,
        isOneTimeProduct,
        isPromoCodeUsed,
        canPauseSubscription,
        handlePauseSubscription,
    } = useSubscriptionCard({
        subscription,
        onCancelSubscription,
        onRestoreSubscription,
        onResumeSubscription,
        onReturnOffer,
        onPauseSubscription,
    });

    const commonProps = {
        feeData,
        tabIcon,
        heading,
        state,
        externalId,
        subscriptionPeriod,
        price,
        isTrial,
        isMainSubscription,
        productCode,
        isReminderSet,
        isUpgrade,
        isNextChargeAvailable,
        isOneTimeProduct,
        isPromoCodeUsed,
    };

    if (isExpiredSubscription) {
        return <ExpiredSubscription {...commonProps} onRestoreSubscription={handleRestoreSubscription} />;
    }

    if (isSubscriptionUpdating) {
        return (
            <UpdatingSubscription
                {...commonProps}
                captions={{
                    description: t('subscription.text.updatingDescription'),
                }}
            />
        );
    }

    if (isSubscriptionRedemption) {
        return (
            <SubscriptionRedemption
                {...commonProps}
                expirationDate={expirationDate}
                captions={{
                    title: t('subscription.text.awaitingPayment'),
                    description: t('subscription.text.payYourBalance'),
                }}
                onCancelSubscription={handleCancelSubscription}
            />
        );
    }

    if (isSubscriptionPaused) {
        return (
            <PausedSubscription
                {...commonProps}
                captions={{
                    title: t('subscription.text.pausedTitle', {
                        pausePeriod: t('basic.periods', {
                            count: subscriptionPausePeriod.count,
                            context: subscriptionPausePeriod.context,
                        }),
                        date: pausedTo,
                    }),
                    description: t('subscription.text.pausedDescription'),
                }}
                nextChargeDate={expirationDate}
                isTimePassedSincePaused={isTimePassedSincePaused}
                onResumeSubscription={handleResumeSubscription}
                onCancelSubscription={handleCancelSubscription}
                isNextChargeAvailable={isNextChargeAvailable}
            />
        );
    }

    if (isExpiresSubscription) {
        return (
            <ExpiresSubscription
                {...commonProps}
                captions={{
                    description: t('subscription.cancellation.expiresSubscription.bannerText', { expirationDate }),
                }}
                onShowReturnOffer={onShowReturnOffer}
                isOfferAvailableForSubscription={isOfferAvailableForSubscription}
                isReturnOfferApplied={isReturnOfferApplied}
            />
        );
    }

    return (
        <DefaultSubscription
            {...commonProps}
            expirationDate={expirationDate}
            state={state}
            upcomingProduct={upcomingProduct}
            onCancelSubscription={handleCancelSubscription}
            isDiscountedSubscription={isDiscountedSubscription}
            isNextChargeAvailable={isNextChargeAvailable}
            canPauseSubscription={canPauseSubscription}
            onPauseSubscription={handlePauseSubscription}
        />
    );
};

export default SubscriptionCard;
